import { useState, useEffect, useCallback } from 'react';
import { api, apiSchemas } from '@verifime/api-definition';
import { OrganisationUser } from '@verifime/components';
import { TNotificationHandler } from '../../types';
import { z } from 'zod';

export type TOrganisationUserWithUsernameAndCredStatus = OrganisationUser & {
  username: string;
  withCredential: boolean;
};

type TModificationRequest = z.infer<typeof apiSchemas.OrganisationModifyUserRequest>;

/**
 * Custom hook to manage organisation user provisioning.
 */
export function useOrganisationUserProvisionData({
  organisationId,
  successNotificationHandler = console.log,
  errorNotificationHandler = console.error,
}: Readonly<{
  organisationId: string;
  successNotificationHandler?: TNotificationHandler;
  errorNotificationHandler?: TNotificationHandler;
}>) {
  const [users, setUsers] = useState<TOrganisationUserWithUsernameAndCredStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!organisationId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await api.getV1organisationIduser({
        params: { id: organisationId },
      });

      setUsers(data as TOrganisationUserWithUsernameAndCredStatus[]);
    } catch (e) {
      setError(e as Error);
      errorNotificationHandler('Failed to load user data.');
    } finally {
      setIsLoading(false);
    }
  }, [organisationId, errorNotificationHandler]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchUsernameFromEmail = async (email: string): Promise<TModificationRequest> => {
    try {
      const response = (await api.getV1userusername({
        queries: { email },
      })) as unknown;
      // api client generation doesn't handle text/plain response, and mapped to void.
      const username = response as string;
      if (!username) {
        throw new Error(`User with email ${email} does not exist`);
      }
      return {
        username,
      };
    } catch (err) {
      throw new Error(`Failed to fetch username for email ${email}`);
    }
  };

  const addRecord = async (
    newRecord: Pick<TOrganisationUserWithUsernameAndCredStatus, 'email'>,
  ) => {
    try {
      const newUser = await fetchUsernameFromEmail(newRecord.email).then(
        async (request: TModificationRequest) => {
          await api.postV1organisationIduser(request, {
            params: { id: organisationId },
          });

          return {
            username: request.username,
            email: newRecord.email,
          };
        },
      );

      setUsers((prev) => [...prev, newUser as TOrganisationUserWithUsernameAndCredStatus]);
      successNotificationHandler('Added');
    } catch (err) {
      errorNotificationHandler(`Failed to add user ${newRecord.email}.`);
      throw err;
    } finally {
      await fetchData();
    }
  };

  const removeRecord = async (userToRemove: TOrganisationUserWithUsernameAndCredStatus) => {
    try {
      await fetchUsernameFromEmail(userToRemove.email).then((request: TModificationRequest) =>
        api.deleteV1organisationIduser(request, {
          params: { id: organisationId },
        }),
      );
      setUsers((prev) => prev.filter((user) => user.email !== userToRemove.email));
      successNotificationHandler('Removed');
    } catch (err) {
      errorNotificationHandler(`Failed to remove user ${userToRemove.email}.`);
      throw err;
    } finally {
      await fetchData();
    }
  };

  return {
    users,
    isLoading,
    error,
    addRecord,
    removeRecord,
  };
}
